// Copyright (c) Docugami, Inc. All rights reserved.

import { ChoiceGroup, DefaultButton, IChoiceGroupOption, Link, Modal } from "@fluentui/react";
import { dgFontFamily } from "components/common/Fonts";
import StatusBar from "components/file_management/status-bar";
import { appContentColor, greyText, modalShadow, primaryThemeSettings, themeBlue } from "components/shell/presentation/AppStyles";
import { ConfirmationDialog } from "components/shell/utils/ConfirmationDialog";
import { RequestState, useAsyncHelper } from "hooks/AsyncHelper";
import { useAppSelector } from "hooks/redux";
import { IWorkPathCommandItem } from "hooks/WorkPathCommands"
import { WorkPathType } from "networking/WorkPathApi";
import { useEffect, useRef, useState } from "react";
import { useUploadAbstractTemplateMutation } from "store/reports/reportsSlice";
import styled from "styled-components";
import { IconRenderer } from "./IconRenderer";
import { WorkPathExportConfigurationModal } from "./WorkPathExportConfigurationModal";
import { DgButton } from "components/common/DgCommonStyledComponents";
import { Telemetry } from "telemetry/TelemetryManager";
import { AbstractTemplateAppliedSchema, AbstractTemplateAppliedWithLegacySchema, TelemetryEvent } from "telemetry/TelemetryEvents";
import { selectReportId, selectReportLabels } from "store/reports/selectors";
import { useGetExportConfiguationQuery } from "store/work_path/workPathSlice";
import { skipToken } from "@reduxjs/toolkit/query";
import { DocsetModelStatsDialog } from "components/reports/DocsetModelStatsDialog";

interface WorkPathCommandVisualProps {
    command: IWorkPathCommandItem
}

export const WorkPathStorageProviderExportModal = (props: WorkPathCommandVisualProps) => {
    const workPathId = useAppSelector(state => state.workPaths.currentWorkPath?.id);
    const workPathName = useAppSelector(state => state.workPaths.currentWorkPath?.name);
    const workPathType = useAppSelector(state => state.workPaths.currentWorkPath?.workPathType);

    const { data: currentConfiguration } = useGetExportConfiguationQuery(!props.command.isModalVisible || !workPathId ? skipToken : workPathId);

    return (
        <WorkPathExportConfigurationModal
            isModalOpen={!!props.command.isModalVisible}
            onClose={() => props.command.onDismiss?.()}
            workPathId={workPathId!}
            configuration={currentConfiguration}
            title={workPathName!}
            workPathType={workPathType!} />
    )
}

export const DocsetModelStatsModal = (props: WorkPathCommandVisualProps) => {
    const docsetId = useAppSelector(state => state.workPaths.currentWorkPath?.clusterId);

    if (!props.command.isModalVisible) {
        return <></>;
    }

    return (
        <DocsetModelStatsDialog
            docsetId={docsetId ?? ''}
            dismissCallback={() => props.command.onDismiss?.()}  />
    )
}

export const EditReportModal = (props: WorkPathCommandVisualProps) => {
    const reportLabels = useAppSelector(selectReportLabels);

    return (
        <ConfirmationDialog
            hidden={!props.command.isModalVisible}
            title='Caution!'
            subText={`Editing your ${reportLabels.titleName.toLocaleLowerCase()} will allow you to add, remove, or change your ${reportLabels.itemsName.toLocaleLowerCase()} items. However, this will require to you review your documents again.`}
            confirmButtonText='Continue'
            dismissButtonText='Cancel'
            dismissedCallback={() => props.command.onDismiss?.()}
            confirmedCallback={() => props.command.trigger?.()}
            styles={!!props.command.disabled ? {
                root: {
                    cursor: 'wait',
                },
                main: {
                    pointerEvents: 'none',
                    opacity: .5
                }
            } : undefined}
        />
    )
}

const StyledConfirmationDialog = styled(ConfirmationDialog)`
    .ms-Dialog-main{
        max-width: 420px;
        max-height: 250px;
    }
    .ms-Dialog-actionsRight {
        display: flex;
        flex-direction: row-reverse;
    }
`

export const RepublishDocumentsModal = (props: WorkPathCommandVisualProps) => {
    return (
        <StyledConfirmationDialog
            hidden={!props.command.isModalVisible}
            title={props.command.title}
            subText={''}
            confirmButtonText={props.command.confirmText}
            dismissButtonText={'Cancel'}
            dismissedCallback={() => props.command.onDismiss?.()}
            confirmedCallback={() => props.command.trigger?.()} />
    )
}

const ContentContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    flex: 1;
    align-items: center;
    text-align: center;
    padding-left: 10%;
    padding-right: 10%;
    gap: 8%;
    box-sizing: border-box;
`;

const InfoText = styled.div`
    font-family: ${dgFontFamily};
    font-size: 30px;
    font-weight: 300;
    line-height: 36px;
    color: ${appContentColor};
`;

const StyledModal = styled(Modal)`
    .ms-Dialog-main {
        border-radius: 20px;
        box-shadow: ${modalShadow};
        width: 75vw;
        height: 75vh;
        overflow: hidden;
    }
    .ms-Modal-scrollableContent {
        width: 100%;
        height: 100%;
        overflow: hidden;
        box-sizing: border-box;
        padding: 34px 34px 20px 34px;
        background-color: ${primaryThemeSettings.palette.white};
        display: flex;
        flex-direction: column;
    }
`;

export interface ConnectorInfoModalProps {
    command: IWorkPathCommandItem
}

export const ConnectorInfoModal = (props: ConnectorInfoModalProps): JSX.Element | null => {
    const workPathType = useAppSelector(state => state.workPaths.currentWorkPath?.workPathType);

    function getInfoVisual() {
        switch (workPathType) {
            case WorkPathType.ZapierWorkFlow: {
                return (
                    <span>
                        Get <Link href="https://zapier.com/apps/docugami/integrations" target='_blank'>Docugami's App for Zapier</Link> and put the data inside your documents to work with 5,000 of the web’s top tools and services.
                    </span>
                );
            }

            case WorkPathType.AutomationAnywhereDocumentAssembly: {
                return "Use the Docugami Automation Anywhere Bot to rapidly assemble new documents using information Docugami extracted from your existing documents.";
            }

            case WorkPathType.AutomationAnywhereWorkFlow: {
                return "Use the Docugami Automation Anywhere Bot to inject information from inside your documents into Automation Anywhere's enterprise Robotic Process Automation ecosystem.";
            }

            case WorkPathType.UiPathDocumentAssembly: {
                return "Use the Docugami UiPath Bot to rapidly assemble new documents using information Docugami extracted from your existing documents.";
            }

            case WorkPathType.UiPathWorkFlow: {
                return "Use the Docugami UiPath Bot to inject information from inside your documents into UiPath's enterprise Robotic Process Automation ecosystem.";
            }

            case WorkPathType.PowerAutomateWorkFlow: {
                return "Get the Docugami Power Automate Connector and put the information inside your documents to work across hundreds of the web's top tools and services.";
            }
        }

        return null;
    }

    return (
        <StyledModal
            isOpen={!!props.command.iconDescriptor && !!props.command.isModalVisible}
            onDismiss={props.command.onDismiss}
            styles={{
                main: {
                    maxWidth: "75%"
                },
                scrollableContent: {
                    overflow: "hidden"
                }
            }}>
            <ContentContainer>
                <IconRenderer source={props.command.iconDescriptor!} width={450} height={300} fallbackColor={themeBlue} />
                <InfoText>
                    {getInfoVisual()}
                </InfoText>
            </ContentContainer>
        </StyledModal>
    );
}

const UploadHelpText = styled.p`
    font-family: ${dgFontFamily};
    font-weight: 500;
    font-size: 18px;
    line-height: 27px;
    text-align: left;
`
const StatusBarContainer = styled.div`
    width: 100%;
    height: 10px;
`

const StyledRepublishChoiceGroup = styled(ChoiceGroup)`
    text-align: left;
    margin-bottom: 40px;
`

const TemplateContentContainer = styled(ContentContainer)`
    padding-top: 10%;
    padding-left: 6%;
    padding-right: 6%;
    justify-content: flex-start;
`

const AbstractTemplateUploadStyledModal = styled(StyledModal)`
    .ms-Modal-scrollableContent {
        padding: 0;
    }
`

const ButtonGroup = styled.div`
    display: flex;
    width: 100%;
    gap: 5px;
    padding-left: 25px;
`

const HiddenInput = styled.input`
    display: none;
`

enum RepublishOptionKeys {
    true = 'true',
    false = 'false'
}

const republishOptions: IChoiceGroupOption[] = [
    { key: RepublishOptionKeys.false, text: 'Only apply this template to future abstracts', },
    { key: RepublishOptionKeys.true, text: 'Apply this template to existing and future abstracts' },
];

interface AbstractUploadModalProps {
    command: IWorkPathCommandItem
}

export const AbstractTemplateUploadModal = (props: AbstractUploadModalProps): JSX.Element | null => {
    const inputFile = useRef<HTMLInputElement | null>(null);
    const [token, setToken] = useState(Date.now());
    const [republish, setRepublish] = useState(republishOptions[0].key);

    const { execute: executeUpload, state: uploadStatus } = useAsyncHelper("Upload template", token);
    const [uploadAbstractTemplate] = useUploadAbstractTemplateMutation();
    const reportId = useAppSelector(selectReportId);

    useEffect(() => {
        if (uploadStatus === RequestState.Complete) {
            setToken(Date.now());
            props.command.onDismiss?.();
        }
    }, [props.command, uploadStatus])

    const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!!event?.target?.files?.[0]) {
            if (republish === RepublishOptionKeys.true) {
                Telemetry.trackEvent<AbstractTemplateAppliedWithLegacySchema>({
                    eventName: TelemetryEvent.AbstractTemplateAppliedWithLegacy
                });
            } else {
                Telemetry.trackEvent<AbstractTemplateAppliedSchema>({
                    eventName: TelemetryEvent.AbstractTemplateApplied
                });
            }
            const file = event.target.files[0];
            const formData = new FormData();
            formData.append("File", file, file.name);
            formData.append("Republish", republish);
            executeUpload(uploadAbstractTemplate({ reportId, formData }))
        }
    };

    const onClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        inputFile.current?.click();
    }

    return (
        <AbstractTemplateUploadStyledModal
            isOpen={!!props.command.iconDescriptor && !!props.command.isModalVisible}
            onDismiss={props.command.onDismiss}
            styles={{
                main: {
                    maxWidth: "471px",
                    maxHeight: "508px"
                },
                scrollableContent: {
                    overflow: "hidden"
                }
            }}>
            <TemplateContentContainer>
                <UploadHelpText>Docugami will import the selected template and apply it to the rest of the Abstracts in the doc set. You will need to refresh your browser to see the changes once they’ve been processed.</UploadHelpText>
                <StyledRepublishChoiceGroup
                    styles={{
                        root: {
                            'label': {
                                'font-size': '18px',
                                'font-weight': 500,
                            },
                        },
                    }}
                    options={republishOptions}
                    selectedKey={republish}
                    onChange={(ev, option) => setRepublish(option?.key ?? 'false')} />
                {uploadStatus === RequestState.InProgress ?
                    <StatusBarContainer>
                        <StatusBar isInfinite={true} />
                    </StatusBarContainer>
                    :
                    (<ButtonGroup>
                        <DefaultButton
                            onClick={props.command.onDismiss}
                            styles={{
                                root: {
                                    color: greyText,
                                    border: 'none'
                                }
                            }} >Cancel</DefaultButton>
                        <DgButton onClick={onClick}>UPLOAD</DgButton>
                        <HiddenInput
                            type='file'
                            id='file'
                            accept=".docx"
                            ref={inputFile}
                            onChange={onFileChange} />
                    </ButtonGroup>)}
            </TemplateContentContainer>
        </AbstractTemplateUploadStyledModal>
    );
}

